import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Factory,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  createEmptyWorkflowStep,
  normalizeWorkflowSteps,
} from '../utils/productionOrders';
import { loadRelationalRecipesIfAvailable } from '../utils/productionRecipes';
import { applyActiveProductsFilter } from '../utils/productStatus';

export default function ProduccionFlujos() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const preferredProductId = searchParams.get('productId') || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [recetasJson, setRecetasJson] = useState([]);
  const [stepEntries, setStepEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(preferredProductId);
  const [workflowDraft, setWorkflowDraft] = useState([]);
  const [workflowNotes, setWorkflowNotes] = useState('');

  const loadData = useCallback(async () => {
    if (!profile?.organization_id) return;

    setLoading(true);
    try {
      const orgId = profile.organization_id;
      const [productsRes, workflowsRes, recetasJsonRes, stepEntriesRes] = await Promise.all([
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, is_active, brand')
          .eq('organization_id', orgId)
        )
          .order('name'),
        supabase
          .from('product_production_workflows')
          .select('*')
          .eq('organization_id', orgId)
          .eq('is_active', true),
        supabase
          .from('recetas')
          .select('*')
          .eq('organization_id', orgId),
        supabase
          .from('production_step_entries')
          .select('id, product_id, step_key, is_active')
          .eq('organization_id', orgId)
          .eq('is_active', true),
      ]);

      if (productsRes.error) throw productsRes.error;
      if (workflowsRes.error) throw workflowsRes.error;
      if (recetasJsonRes.error) throw recetasJsonRes.error;
      if (stepEntriesRes.error) throw stepEntriesRes.error;

      const recetasRows = recetasJsonRes.data || [];
      const relationalRecipes =
        recetasRows.length > 0
          ? []
          : await loadRelationalRecipesIfAvailable({
              supabase,
              organizationId: orgId,
            });

      const productRows = productsRes.data || [];
      setProducts(productRows);
      setWorkflows(workflowsRes.data || []);
      setRecetasJson(recetasRows);
      setRecipes(relationalRecipes);
      setStepEntries(stepEntriesRes.data || []);

      setSelectedProductId((current) => current || preferredProductId || productRows[0]?.id || '');
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar los flujos de produccion.');
    } finally {
      setLoading(false);
    }
  }, [preferredProductId, profile?.organization_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredProducts = useMemo(() => {
    const normalized = String(searchTerm || '').trim().toLowerCase();
    if (!normalized) return products;
    return products.filter((product) => product.name.toLowerCase().includes(normalized));
  }, [products, searchTerm]);

  const workflowByProductId = useMemo(
    () => new Map(workflows.map((workflow) => [workflow.product_id, workflow])),
    [workflows],
  );

  const selectedProduct = useMemo(
    () => products.find((product) => product.id === selectedProductId) || null,
    [products, selectedProductId],
  );

  const recipeCapableProductIds = useMemo(() => {
    const ids = new Set();
    recipes.forEach((recipe) => {
      if (recipe?.product_id) ids.add(recipe.product_id);
    });
    recetasJson.forEach((recipe) => {
      if (recipe?.producto_id) ids.add(recipe.producto_id);
    });
    return ids;
  }, [recipes, recetasJson]);

  const recipeOptions = useMemo(
    () =>
      products
        .filter((product) => recipeCapableProductIds.has(product.id))
        .map((product) => ({ value: product.id, label: product.name })),
    [products, recipeCapableProductIds],
  );

  useEffect(() => {
    const workflow = selectedProductId ? workflowByProductId.get(selectedProductId) || null : null;
    const existingSteps = normalizeWorkflowSteps(workflow?.steps);
    setWorkflowDraft(
      existingSteps.length > 0
        ? existingSteps
        : [createEmptyWorkflowStep(0), createEmptyWorkflowStep(1)],
    );
    setWorkflowNotes(workflow?.notes || '');
  }, [selectedProductId, workflowByProductId]);

  const handleWorkflowStepChange = (index, field, value) => {
    setWorkflowDraft((current) =>
      current.map((step, stepIndex) => {
        if (stepIndex !== index) return step;
        if (field === 'recipeProductId') {
          const option = recipeOptions.find((candidate) => candidate.value === value);
          return {
            ...step,
            recipeProductId: value,
            recipeProductName: option?.label || '',
          };
        }

        return {
          ...step,
          [field]: value,
        };
      }),
    );
  };

  const handleAddWorkflowStep = () => {
    setWorkflowDraft((current) => [...current, createEmptyWorkflowStep(current.length)]);
  };

  const handleRemoveWorkflowStep = (index) => {
    const stepToRemove = workflowDraft[index];
    if (!stepToRemove) return;

    const hasEntries = stepEntries.some(
      (entry) =>
        entry.product_id === selectedProductId &&
        entry.step_key === stepToRemove.key &&
        entry.is_active,
    );
    if (hasEntries) {
      toast.error('No puedes quitar un paso que ya tiene movimientos activos.');
      return;
    }

    setWorkflowDraft((current) => current.filter((_, stepIndex) => stepIndex !== index));
  };

  const handleSaveWorkflow = async () => {
    if (!profile?.organization_id || !selectedProductId) {
      toast.error('Selecciona un producto.');
      return;
    }

    const normalized = normalizeWorkflowSteps(workflowDraft).map((step, index) => ({
      ...step,
      key: step.key || `paso-${index + 1}`,
      name: step.name || `Paso ${index + 1}`,
    }));

    if (normalized.length === 0) {
      toast.error('Agrega al menos un paso.');
      return;
    }

    const validKeys = new Set(normalized.map((step) => step.key));
    const orphanedEntries = stepEntries.filter(
      (entry) =>
        entry.product_id === selectedProductId &&
        entry.is_active &&
        !validKeys.has(entry.step_key),
    );
    if (orphanedEntries.length > 0) {
      toast.error('Hay movimientos activos en pasos que intentas quitar.');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('product_production_workflows').upsert(
        {
          organization_id: profile.organization_id,
          product_id: selectedProductId,
          steps: normalized,
          notes: workflowNotes || '',
          is_active: true,
        },
        { onConflict: 'organization_id,product_id' },
      );

      if (error) throw error;

      toast.success('Flujo guardado.');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('No se pudo guardar el flujo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">
          Cargando flujos de produccion...
        </p>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/sistema/pedidos-produccion')}
            className="w-10 h-10 rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#51A7FD] transition-all flex items-center justify-center shrink-0"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Factory size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Flujos de productos
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Configuracion predefinida del flujo de produccion por producto.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCcw size={14} /> Actualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,_1fr)] gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar producto..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-9 pr-4 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD]"
              />
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {filteredProducts.map((product) => {
              const workflow = workflowByProductId.get(product.id) || null;
              const stepsCount = normalizeWorkflowSteps(workflow?.steps).length;
              const isActive = product.id === selectedProductId;

              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => setSelectedProductId(product.id)}
                  className={`w-full px-4 py-3 border-b border-slate-100 text-left transition-all ${
                    isActive ? 'bg-blue-50/60' : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  <p className="text-[11px] font-black uppercase tracking-tight text-slate-800">{product.name}</p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {product.brand && (
                      <span className="inline-flex items-center rounded-xl bg-violet-100 text-violet-700 px-2 py-1 text-[8px] font-black uppercase tracking-wide">
                        {product.brand}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center rounded-xl border px-2.5 py-1 text-[8px] font-black uppercase tracking-widest ${
                        stepsCount > 0
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      {stepsCount > 0 ? `${stepsCount} paso(s)` : 'Sin flujo'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Producto</p>
                <h2 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">
                  {selectedProduct?.name || 'Selecciona un producto'}
                </h2>
              </div>

              <button
                type="button"
                onClick={handleAddWorkflowStep}
                disabled={!selectedProductId}
                className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-emerald-700 transition-all hover:bg-emerald-100 disabled:opacity-60"
              >
                <Plus size={12} className="inline mr-1" /> Paso
              </button>
            </div>

            {selectedProductId ? (
              <>
                <div className="mt-4 space-y-3">
                  {workflowDraft.map((step, index) => (
                    <div key={step.key || index} className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-[72px_minmax(0,_1fr)_170px_170px_auto]">
                      <div className="rounded-2xl bg-white px-3 py-3 text-center border border-slate-200">
                        <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Paso</p>
                        <p className="mt-1 text-sm font-black text-slate-700">{index + 1}</p>
                        {index === workflowDraft.length - 1 ? (
                          <p className="mt-1 text-[8px] font-black uppercase tracking-widest text-emerald-600">Final</p>
                        ) : null}
                      </div>

                      <input
                        type="text"
                        value={step.name}
                        onChange={(event) => handleWorkflowStepChange(index, 'name', event.target.value)}
                        placeholder="Nombre del paso"
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                      />

                      <select
                        value={step.type}
                        onChange={(event) => handleWorkflowStepChange(index, 'type', event.target.value)}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                      >
                        <option value="produccion">Produccion</option>
                        <option value="empaquetado">Empaquetado</option>
                        <option value="custom">Paso adicional</option>
                      </select>

                      <select
                        value={step.recipeProductId || ''}
                        onChange={(event) => handleWorkflowStepChange(index, 'recipeProductId', event.target.value)}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                      >
                        <option value="">Sin receta</option>
                        {recipeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => handleRemoveWorkflowStep(index)}
                        className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-rose-600 transition-all hover:bg-rose-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                <textarea
                  value={workflowNotes}
                  onChange={(event) => setWorkflowNotes(event.target.value)}
                  placeholder="Notas internas del flujo"
                  className="mt-4 h-24 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD] resize-none"
                />

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveWorkflow}
                    disabled={saving}
                    className="rounded-2xl bg-[#3FF2AA] px-5 py-3 text-[10px] font-black uppercase tracking-widest text-[#0B2A36] shadow-lg transition-all hover:scale-[1.01] disabled:opacity-60"
                  >
                    <Save size={14} className="inline mr-1" />
                    {saving ? 'Guardando...' : 'Guardar flujo'}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-[10px] font-bold text-slate-500">
                Selecciona un producto para configurar su flujo.
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Como funciona</p>
            <div className="mt-3 space-y-2 text-[10px] font-bold text-slate-500">
              <p>El ultimo paso del flujo se toma como cierre de produccion.</p>
              <p>Si a un paso le asignas una receta, al registrar ese paso se descuenta la materia prima del deposito origen.</p>
              <p>Cuando el flujo completa una nueva cantidad, el sistema cierra esa produccion y suma stock en el deposito destino.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
